import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import Login from './pages/Login';
import Chat from './pages/Chat';
import ChatAnonim from './pages/ChatAnonim';
import Onboarding from './pages/Onboarding';
import Tracker from './pages/Tracker';
import Acasa from './pages/Acasa';
import Plan from './pages/Plan';
import Profil from './pages/Profil';
import Admin from './pages/Admin';
import Termeni from './pages/Termeni';
import Confidentialitate from './pages/Confidentialitate';
import Cookies from './pages/Cookies';
import ResetParola from './pages/ResetParola';
import ResetParolaCerere from './pages/ResetParolaCerere';
import AdminWaitlist from './pages/AdminWaitlist';
import AdminUsers from './pages/AdminUsers';
import LandingPage from './pages/LandingPage';
import AccessCheck from './pages/AccessCheck';
import Layout from './components/Layout';
import CookieBanner from './components/CookieBanner';
import api, { reactivareConsimtamant } from './services/api';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLogat } = useAuthStore();
  return isLogat ? <>{children}</> : <Navigate to="/login" />;
};

const WithLayout: React.FC<{ children: React.ReactNode, areProfil: boolean | null }> = ({ children, areProfil }) => {
  if (areProfil === false) return <Navigate to="/onboarding" />;
  return <Layout>{children}</Layout>;
};

const ContSuspendat: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [mesaj, setMesaj] = useState('');

  const handleReactivare = async () => {
    setLoading(true);
    try {
      await reactivareConsimtamant();
      window.location.reload();
    } catch {
      setMesaj('Eroare. Încearcă din nou.');
      setLoading(false);
    }
  };

  const handleSterge = () => {
    if (window.confirm('Ești sigur? Această acțiune este IREVERSIBILĂ. Toate datele tale vor fi șterse permanent.')) {
      if (window.confirm('Ultima confirmare — ștergem contul și toate datele tale?')) {
        const token = localStorage.getItem('token');
        fetch(`${process.env.REACT_APP_API_URL}/gdpr/sterge-cont`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }).then(() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        });
      }
    }
  };

  return (
    <div style={{ background: '#12121f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'DM Sans, sans-serif' }}>
      <div style={{ maxWidth: '480px', width: '100%', padding: '2rem', textAlign: 'center' }}>
        <p style={{ fontSize: '3rem', margin: '0 0 1rem' }}>⏸️</p>
        <h2 style={{ color: '#fff', marginBottom: '0.5rem' }}>Procesarea datelor suspendată</h2>
        <p style={{ color: '#8080a0', marginBottom: '2rem', lineHeight: 1.6 }}>
          Ai retras consimțământul pentru procesarea datelor tale. Nu mai poți folosi funcțiile Flexara cât timp consimțământul este retras.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <button onClick={handleReactivare} disabled={loading} style={{
            padding: '0.75rem', borderRadius: '10px', border: 'none',
            background: loading ? '#2a2a4a' : '#4a4ad8', color: '#fff',
            cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.95rem',
            opacity: loading ? 0.7 : 1
          }}>
            {loading ? 'Se procesează...' : 'Reactivează consimțământul'}
          </button>
          <button onClick={handleSterge} style={{
            padding: '0.75rem', borderRadius: '10px',
            border: '1px solid #4a1a1a', background: '#2a0a0a',
            color: '#d06060', cursor: 'pointer', fontSize: '0.95rem'
          }}>
            Șterge contul și toate datele
          </button>
          <button onClick={() => { localStorage.removeItem('token'); window.location.href = '/login'; }} style={{
            padding: '0.5rem', borderRadius: '8px', border: 'none',
            background: 'transparent', color: '#6060a0', cursor: 'pointer', fontSize: '0.85rem'
          }}>
            Deconectare
          </button>
        </div>
        {mesaj && <p style={{ color: '#d06060', marginTop: '1rem', fontSize: '0.85rem' }}>{mesaj}</p>}
      </div>
    </div>
  );
};

const AppRoutes: React.FC = () => {
  const { isLogat } = useAuthStore();
  const [areProfil, setAreProfil] = useState<boolean | null>(null);
  const [consimtamantRetras, setConsimtamantRetras] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const navigate = useNavigate();

  const incarcaProfil = () => {
    setAreProfil(null);
    setLoadError(false);
    api.get('/profil/get')
      .then(res => {
        setConsimtamantRetras(!!(res.data?.consimtamant_retras));
        setAreProfil(!!(res.data && res.data?.nume));
      })
      .catch(() => {
        setTimeout(() => {
          api.get('/profil/get')
            .then(res => {
              setConsimtamantRetras(!!(res.data?.consimtamant_retras));
              setAreProfil(!!(res.data && res.data?.nume));
            })
            .catch(() => setLoadError(true));
        }, 2000);
      });
  };

  useEffect(() => {
    if (!isLogat) return;
    incarcaProfil();
  }, [isLogat]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLogat && loadError) {
    return (
      <div style={{ background: '#12121f', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
        <p style={{ color: '#d06060' }}>Eroare de conexiune.</p>
        <button onClick={incarcaProfil} style={{ padding: '0.6rem 1.4rem', borderRadius: '8px', background: '#4a4ad8', color: '#fff', border: 'none', cursor: 'pointer' }}>
          Reîncearcă
        </button>
      </div>
    );
  }

  if (isLogat && areProfil === null) {
    return (
      <div style={{ background: '#12121f', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#e0e0f0' }}>Se încarcă...</p>
      </div>
    );
  }

  if (isLogat && consimtamantRetras) {
    return <ContSuspendat />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/access" element={<AccessCheck />} />
      <Route path="/chat-anonim" element={<ChatAnonim />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <Onboarding onComplete={() => { setAreProfil(true); navigate('/app', { replace: true }); }} />
        </ProtectedRoute>
      } />
      <Route path="/" element={<LandingPage />} />
      <Route path="/app" element={
        <ProtectedRoute><WithLayout areProfil={areProfil}><Acasa /></WithLayout></ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute><WithLayout areProfil={areProfil}><Chat /></WithLayout></ProtectedRoute>
      } />
      <Route path="/tracker" element={
        <ProtectedRoute><WithLayout areProfil={areProfil}><Tracker /></WithLayout></ProtectedRoute>
      } />
      <Route path="/plan" element={
        <ProtectedRoute><WithLayout areProfil={areProfil}><Plan /></WithLayout></ProtectedRoute>
      } />
      <Route path="/profil" element={
        <ProtectedRoute><WithLayout areProfil={areProfil}><Profil /></WithLayout></ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute><WithLayout areProfil={areProfil}><Admin /></WithLayout></ProtectedRoute>
      } />
      <Route path="/reset-parola" element={<ResetParola />} />
      <Route path="/reset-parola-cerere" element={<ResetParolaCerere />} />
      <Route path="/termeni" element={<Termeni />} />
      <Route path="/confidentialitate" element={<Confidentialitate />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/admin/waitlist" element={
        <ProtectedRoute><AdminWaitlist /></ProtectedRoute>
      } />
      <Route path="/admin/users" element={
        <ProtectedRoute><AdminUsers /></ProtectedRoute>
      } />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Toaster position="top-center" toastOptions={{
        style: { background: '#1a1a30', color: '#e0e0f0', border: '1px solid #2a2a4a' }
      }} />
      <AppRoutes />
      <CookieBanner />
    </BrowserRouter>
  );
};

export default App;